import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import LandingPage from "./pages/LandingPage";
import TemplatesPage from "./pages/TemplatesPage";
import EditorPage from "./pages/EditorPage";
import DashboardPage from "./pages/DashboardPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import CheckoutCancelPage from "./pages/CheckoutCancelPage";
import SharePage from "./pages/SharePage";
import CartPage from "./pages/CartPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import SMSGettingStarted from "./pages/SMSGettingStarted";
import AuthCallback from "./components/AuthCallback";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminTemplates from "./pages/admin/AdminTemplates";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminPricing from "./pages/admin/AdminPricing";
import AdminFonts from "./pages/admin/AdminFonts";
import AdminTransactions from "./pages/admin/AdminTransactions";
import AdminCustomerDetails from "./pages/admin/AdminCustomerDetails";
import AdminOrderDetails from "./pages/admin/AdminOrderDetails";
import AdminIvrPage from "./pages/admin/AdminIvrPage";
import AdminErrorLogs from "./pages/admin/AdminErrorLogs";
import AdminPartners from "./pages/admin/AdminPartners";
import AdminCalls from "./pages/admin/AdminCalls";
import AdminSmsInbox from "./pages/admin/AdminSmsInbox";
import PartnerPortal from "./pages/partner/PartnerPortal";
import PartnerDesigner from "./pages/partner/PartnerDesigner";
import NotFoundPage from "./pages/NotFoundPage";
import CatalogPage from "./pages/CatalogPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import { PageLoader } from "./components/Spinner";
import "./App.css";

// Scroll to the top of the page on navigation (but keep in-page anchors working).
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <PageLoader label="Loading your account" />;
  }

  if (!user) {
    // Remember where the visitor was going so we can send them back after sign-in.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  
  if (adminOnly && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/templates" element={<TemplatesPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/auth/google" element={<AuthCallback />} />
            <Route path="/share/:shareLink" element={<SharePage />} />
            <Route path="/catalog/:categoryId" element={<CatalogPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/sms-getting-started" element={<SMSGettingStarted />} />
            
            <Route path="/editor/:templateId" element={
              <ProtectedRoute>
                <EditorPage />
              </ProtectedRoute>
            } />
            <Route path="/editor/invitation/:invitationId" element={
              <ProtectedRoute>
                <EditorPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/cart" element={
              <ProtectedRoute>
                <CartPage />
              </ProtectedRoute>
            } />
            <Route path="/checkout" element={
              <ProtectedRoute>
                <CheckoutPage />
              </ProtectedRoute>
            } />
            <Route path="/checkout/success" element={
              <ProtectedRoute>
                <CheckoutSuccessPage />
              </ProtectedRoute>
            } />
            <Route path="/checkout/cancel" element={
              <ProtectedRoute>
                <CheckoutCancelPage />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/templates" element={
              <ProtectedRoute adminOnly>
                <AdminTemplates />
              </ProtectedRoute>
            } />
            <Route path="/admin/categories" element={
              <ProtectedRoute adminOnly>
                <AdminCategories />
              </ProtectedRoute>
            } />
            <Route path="/admin/ivr" element={<Navigate to="/admin/ivr/field-inputs" replace />} />
            <Route path="/admin/ivr/:page" element={
              <ProtectedRoute adminOnly>
                <AdminIvrPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/customers" element={
              <ProtectedRoute adminOnly>
                <AdminCustomers />
              </ProtectedRoute>
            } />
            <Route path="/admin/customers/:customerId" element={
              <ProtectedRoute adminOnly>
                <AdminCustomerDetails />
              </ProtectedRoute>
            } />
            <Route path="/admin/orders" element={
              <ProtectedRoute adminOnly>
                <AdminOrders />
              </ProtectedRoute>
            } />
            <Route path="/admin/orders/:orderId" element={
              <ProtectedRoute adminOnly>
                <AdminOrderDetails />
              </ProtectedRoute>
            } />
            <Route path="/admin/pricing" element={
              <ProtectedRoute adminOnly>
                <AdminPricing />
              </ProtectedRoute>
            } />
            <Route path="/admin/fonts" element={
              <ProtectedRoute adminOnly>
                <AdminFonts />
              </ProtectedRoute>
            } />
            <Route path="/admin/transactions" element={
              <ProtectedRoute adminOnly>
                <AdminTransactions />
              </ProtectedRoute>
            } />
            <Route path="/admin/error-logs" element={
              <ProtectedRoute adminOnly>
                <AdminErrorLogs />
              </ProtectedRoute>
            } />
            <Route path="/admin/partners" element={
              <ProtectedRoute adminOnly>
                <AdminPartners />
              </ProtectedRoute>
            } />
            <Route path="/admin/calls" element={
              <ProtectedRoute adminOnly>
                <AdminCalls />
              </ProtectedRoute>
            } />
            <Route path="/admin/sms-inbox" element={
              <ProtectedRoute adminOnly>
                <AdminSmsInbox />
              </ProtectedRoute>
            } />

            {/* Partner portal: invitation sites design their own templates (needs admin approval) */}
            <Route path="/partner" element={
              <ProtectedRoute>
                <PartnerPortal />
              </ProtectedRoute>
            } />
            <Route path="/partner/templates/:templateId" element={
              <ProtectedRoute>
                <PartnerDesigner />
              </ProtectedRoute>
            } />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
